import {connect} from "react-redux"
import {REALM_HEADER} from "@next/utils"
import {createSelector} from "reselect"
import State from "../../models/state"
import {formatPdpLink, getToolTipTitleByType} from "../../utils/colourwayBuilders"
import {getShowFitIconsByType} from "../../utils/getShowFitIconsByType"
import {selectPriceType} from "../../utils/priceFormatter/selectPriceType"
import {ColourwayData} from "../../models/searchApi"
import {SofaType} from "../../config/constants"
import {getProductImageUrl} from "../../utils/getImageUrls"
import {SofaColourwayData} from "../../models/searchApi/sofa"
import getTitleUtil from "../../utils/getTitleUtil"
import {fetchAdditionalColourchipsThunk} from "../../ducks/productSummary"

const selectColourways = (state: State) => state.productSummary.summaryData.colourways
const selectType = (state: State) => state.productSummary.summaryData.type
const selectImageCdnUrl = (state: State) => state.productSummary.summaryData.imageCdnUrl
const selectBaseUrl = (state: State) => state.productSummary.summaryData.baseUrl
const selectTitle = (state: State) => state.productSummary.summaryData.title
const selectCurrencyCode = (state: State) => state.productSummary.summaryData.currencyCode
const selectDepartment = (state: State) => state.productSummary.summaryData.department
const selectFit = (state: State) => state.productSummary.summaryData.fit
const selectIsEnglishLang = (state: State) => state.request.isEnglishLang
const selectText = (state: State) => state.text
const selectEnabledSearchDesc = (state: State) => state.productSummary.enabledSearchDesc
const selectProductImageUrlPart = (state: State) => state.productSummary.summaryData.productImageUrlPart
const selectLgImagePath = (state: State) => state.productSummary.summaryData.lgImagePath
const selectIm = (state: State) => state.request.im
const selectAkamaiImageParams = (state: State) => state.productSummary.akamaiImageParams
const selectUsePDPImage = (state: State) => state.productSummary.summaryData.usePDPImage
const selectLazyLoad = (state: State) => state.lazyload
const selectDisplayNewIn = (state: State) => state.productSummary.displayNewIn
const selectShowyNewIn = (state: State) => state.productSummary.summaryData.showNewIn
const selectSaleSashPosition = (state: State) => state.productSummary.summaryData.saleSashPosition
const selectDisplayFitIcons = (state: State) => state.productSummary.displayFitIcons
const selectBrand = (state: State) => state.productSummary.summaryData.brand
const selectClient = (state: State) => state.request.headers[REALM_HEADER] as string
const selectUseProductAggregator = (state: State) => state.productSummary.useProductAggregator

const slidesSelector = createSelector(
    [
        selectColourways,
        selectType,
        selectImageCdnUrl,
        selectBaseUrl,
        selectTitle,
        selectCurrencyCode,
        selectDepartment,
        selectFit,
        selectIsEnglishLang,
        selectText,
        selectEnabledSearchDesc,
        selectProductImageUrlPart,
        selectLgImagePath,
        selectIm,
        selectAkamaiImageParams,
        selectUsePDPImage,
        selectLazyLoad,
        selectDisplayNewIn,
        selectShowyNewIn,
        selectSaleSashPosition,
        selectDisplayFitIcons,
        selectBrand,
        selectClient,
        selectUseProductAggregator,
    ],
    (
        colourways,
        type,
        imageCdnUrl,
        baseUrl,
        defaultTitle,
        currencyCode,
        department,
        fit,
        isEnglishLang,
        text,
        enabledSearchDesc,
        productImageUrlPart,
        lgImagePath,
        im,
        akamaiImageParams,
        usePDPImage,
        lazyload,
        displayNewIn,
        showNewIn,
        saleSashPosition,
        displayFitIcons,
        brand,
        client,
        useProductAggregator,
    ) => {
        let currentSlideIndex = 0

        const slides = (colourways as ColourwayData[]).map((colourway, index) => {
            const isColourwayOnSale = Boolean(colourway?.sp)
            const textTitle = (
                useProductAggregator
                    ? colourway?.t
                    : getTitleUtil({
                          type,
                          enabledSearchDesc,
                          title: defaultTitle,
                          selectedColourway: colourway,
                          fit,
                          department,
                          isEnglishLang,
                      })
            ) as string

            if (colourway.s) {
                currentSlideIndex = index
            }
            const {id, t: title, p: price, url, sp: salePrice} = colourway

            let mainImage = ""
            let colourChipImage = ""
            if (type === SofaType) {
                mainImage = (colourway as SofaColourwayData).mi
                colourChipImage = (colourway as SofaColourwayData).cci
            }

            return {
                id,
                imageUrl: getProductImageUrl({
                    imageCdnUrl,
                    productImageUrlPart,
                    lgImagePath,
                    id,
                    type,
                    mainImage,
                    akamaiImageQueryParams: akamaiImageParams.sm,
                    im,
                    usePDPImage,
                }),
                tooltipTitle: getToolTipTitleByType(type, {
                    title,
                    defaultTitle: textTitle,
                    colourwayItemNumber: id,
                    price,
                    salePrice,
                    fit,
                    selectedColourway: colourway,
                    isEnglishLang,
                }),
                linkUrl: formatPdpLink(baseUrl, url),
                lazyloadProductImages: lazyload.productImages,
                textTitle,
                colour: colourway.c,
                price: selectPriceType(price, salePrice),
                currencyCode,
                department,
                displayNewIn: displayNewIn && index === 0 && showNewIn && !isColourwayOnSale,
                showFitsIcons: getShowFitIconsByType(
                    type,
                    department,
                    {
                        isOnSale: isColourwayOnSale,
                        saleSashPosition,
                    },
                    displayFitIcons,
                ),
                isColourwayOnSale,
                fits: colourway.f,
                text,
                summaryType: type,
                brand,
                client,
            }
        })
        return {
            slides,
            currentSlideIndex,
        }
    },
)

export const mapStateToProps = (state: State) => {
    const {
        productSummary: {
            summaryData: {colourways, currencyCode},
            colourchipSelected,
            mobileThumbnailConfig,
        },
        lazyload,
        text,
        textAlignment,
        template,
    } = state

    const {currentSlideIndex} = slidesSelector(state)
    const selectedColourway = colourways[currentSlideIndex]

    return {
        slides: slidesSelector(state).slides,
        currentSlideIndex,
        lazyloadProductImages: lazyload.productImages,
        colour: selectedColourway.c,
        price: selectPriceType(selectedColourway.p, selectedColourway.sp),
        currencyCode,
        text,
        textAlignment,
        colourchipSelected,
        mobileThumbnailConfig,
        template,
    }
}

const mapDispatchToProps = {
    fetchAdditionalColourchipsThunk,
}

export default connect(mapStateToProps, mapDispatchToProps)
